import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { theme, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import { managementOp } from 'store/ducks/Management';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { IProduct } from 'store/ducks/Management/types';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import DataManagementLayout from 'components/Layouts/DataManagementLayout/DataManagementLayout';
import useDeleteWithDependencyCheck from 'utils/hooks/useDeleteWithDependencyCheck';
import * as Styled from './ProductManagement.styled';

const { useToken } = theme;

const ProductManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = useToken();

  const dispatch = useDispatch();

  const { drawTable, lang, product, saveClicked } = useSelector(
    (state: RootState) => ({
      drawTable: state.management.drawTable,
      lang: state.global.lang,
      product: state.management.product,
      saveClicked: state.management.saveClicked
    })
  );

  const [rows, setRows] = useState<IProduct[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [modifiedRowIds, setModifiedRowIds] = useState<Set<number>>(new Set());
  const [initialRows, setInitialRows] = useState<IProduct[]>([]);

  const hasUnsavedChanges = useMemo(() => {
    if (initialRows.length === 0 && rows.length === 0) {
      return false;
    }
    const rowsChanged = JSON.stringify(rows) !== JSON.stringify(initialRows);
    const hasChangesSet = hasChanges;
    const hasModifiedRows = modifiedRowIds.size > 0;
    return rowsChanged || hasChangesSet || hasModifiedRows;
  }, [rows, initialRows, hasChanges, modifiedRowIds]);

  useUnsavedChanges({ hasChanges: hasUnsavedChanges });

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const handleFieldChange = (
    rowKey: string,
    field: keyof IProduct,
    value: string
  ) => {
    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.key === rowKey ? { ...row, [field]: value } : row
      );

      // Track modified existing rows by pfn_id
      const updatedRow = updatedRows.find((row) => row.key === rowKey);
      if (updatedRow?.pfn_id) {
        setModifiedRowIds((prev) => new Set(prev).add(updatedRow.pfn_id!));
      }

      return updatedRows;
    });
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  useEffect(() => {
    if (product && Array.isArray(product)) {
      const mappedRows = product.map(
        (
          prod: { id?: number; short_name: string; description: string },
          index: number
        ) => ({
          key: String(index + 1),
          pfn_id: prod.id, // Backend sends 'id', map to 'pfn_id'
          short_name: prod.short_name,
          description: prod.description
        })
      );

      setRows(mappedRows);

      setModifiedRowIds(new Set());
      setHasChanges(false);

      if (mappedRows.length > 0) {
        setInitialRows(mappedRows);
      }
    }
  }, [product, lang]);

  // Enable/disable save based on changes
  useEffect(() => {
    dispatch(managementOp.setDataEdited(hasChanges));
  }, [hasChanges, dispatch]);

  const handleSave = async () => {
    try {
      const modifiedRows = rows
        .filter((row) => {
          const isNewRow = !row.pfn_id;
          const isModifiedRow = row.pfn_id && modifiedRowIds.has(row.pfn_id);
          return isNewRow || isModifiedRow;
        })
        .filter((row) => row.short_name.trim() || row.description.trim())
        .map((row) => {
          if (row.pfn_id) {
            return {
              pfn_id: row.pfn_id,
              short_name: row.short_name,
              description: row.description
            };
          } else {
            return {
              pfn_id: generateUniqueId(),
              short_name: row.short_name,
              description: row.description
            };
          }
        });

      if (modifiedRows.length === 0) {
        message.info('No changes to save');
        return;
      }

      const saveResponse = await ApiService.saveProduct(
        { products: modifiedRows },
        lang
      );

      const refreshedData = await ApiService.getProduct(lang, {});
      if (refreshedData?.data) {
        dispatch(managementOp.setProduct(refreshedData.data));
      }

      setHasChanges(false);
      setModifiedRowIds(new Set());
      dispatch(managementOp.setDataEdited(false));
      setInitialRows(rows);

      message.success(
        saveResponse?.data?.message || 'Products saved successfully!'
      );
    } catch (error: any) {
      console.error('Save failed:', error);
      message.error(
        error?.response?.data?.message || 'Failed to save products'
      );
    }
  };

  useEffect(() => {
    if (saveClicked) {
      handleSave();
      dispatch(managementOp.setSaveClicked(false));
    }
  }, [saveClicked]);

  const handleAddRow = () => {
    const newRow: IProduct = {
      key: String(rows.length + 1),
      short_name: '',
      description: ''
    };
    setRows((prevRows) => [...prevRows, newRow]);
    setHasChanges(true);
    dispatch(managementOp.setDataEdited(true));
  };

  const { handleDelete: handleProductDelete } = useDeleteWithDependencyCheck({
    tableName: 'product_functions',
    softDeleteFn: (id) => ApiService.softDeleteRecord('product_functions', id),
    onSuccess: async () => {
      const refreshedData = await ApiService.getProduct(lang, {});
      if (refreshedData?.data) {
        dispatch(managementOp.setProduct(refreshedData.data));
      }
    },
    entityLabel: 'product',
    lang
  });

  const columns: ColumnsType<IProduct> = useMemo(
    () => [
      {
        title: t('ACTION'),
        key: 'action',
        width: 100,
        render: (_, record) => (
          <Styled.StyledButtonSection token={token}>
            <Styled.StyledButton
              type='primary'
              size='small'
              shape='circle'
              icon={<DeleteOutlined />}
              disabled={!record.pfn_id}
              onClick={() =>
                record.pfn_id && handleProductDelete(record.pfn_id)
              }
            />
          </Styled.StyledButtonSection>
        )
      },
      {
        title: t('PRODUCT_NAME'),
        dataIndex: 'short_name',
        key: 'short_name',
        width: 425,
        render: (val, data) => (
          <Styled.ColumnTextBox
            width={425}
            value={data.short_name}
            placeholder='Enter short name'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'short_name', e.target.value)
            }
          />
        )
      },
      {
        title: t('DESCRIPTION'),
        dataIndex: 'description',
        key: 'description',
        width: 425,
        render: (val, data) => (
          <Styled.ColumnTextBox
            width={425}
            value={data.description}
            placeholder='Enter description'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange(data.key!, 'description', e.target.value)
            }
          />
        )
      }
    ],
    [t, token]
  );

  return (
    <DataManagementLayout>
      <Styled.ContentWrapper>
        {drawTable ? (
          <>
            <CustomTable
              drawTable={drawTable}
              columns={columns}
              dataSource={rows}
              itemsCount={rows.length}
              pagination={false}
              scroll={{ x: 1000 }}
            />
            <Styled.AddButton
              type='primary'
              onClick={handleAddRow}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </>
        ) : (
          <CustomTable
            drawTable={drawTable}
            itemsCount={0}
            columns={columns}
            dataSource={[]}
            pagination={false}
          />
        )}
      </Styled.ContentWrapper>
    </DataManagementLayout>
  );
};

export default ProductManagement;
